import React from 'react';
import { Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { UniversalTable } from '../AccountTable';
import { Offer as ApiOffer, TrafficSource } from '../../api/staff';
import Typography from '../typography/Typography';
import IdView from '../id-view';
import { useTranslations } from '../../contexts/translation.context';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import {
  OnlyFansIc,
  InstagramIc,
  GoogleIc,
  TwitterIc,
  IcReddit,
  IcAnotherSoc,
  IcTg,
  IcTikTok,
  IcYoutube,
  InfoCircle,
  UserCheckIc,
  MoneyIc,
} from '../icons';

const columns = [
  { key: 'id', name: 'ID' },
  { key: 'marketer', name: 'Маркетолог' },
  { key: 'trafficSource', name: 'Источник траффика' },
  { key: 'status', name: 'Статус' },
  { key: 'actions', name: '' },
];

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'active':
      return {
        label: 'Активный',
        dotColor: '#17c964',
      };
    case 'deactivated':
      return {
        label: 'Деактивирован',
        dotColor: '#A1A1AA',
      };
    case 'hidden':
      return {
        label: 'Скрыт',
        dotColor: '#71717A',
      };
    case 'moderation':
      return {
        label: 'На модерации',
        dotColor: '#E8A323',
      };
    default:
      return {
        label: status,
        dotColor: '#71717A',
      };
  }
};

const getMarketerLevelConfig = (lvl: string) => {
  switch (lvl.toLowerCase()) {
    case 'new':
      return {
        label: 'New',
        bgColor: 'rgba(23, 201, 100, 0.2)',
        textColor: '#17c964',
      };
    case 'base':
      return {
        label: 'Base',
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
    case 'pro':
      return {
        label: 'PRO',
        bgColor: '#11181C',
        textColor: '#FFFFFF',
      };
    case 'expert':
      return {
        label: 'Expert',
        bgColor: 'rgba(120, 40, 200, 0.2)',
        textColor: '#7828c8',
      };
    default:
      return {
        label: lvl,
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
  }
};

// Helper function to format campaign types
const formatCampaignTypes = (types?: ('free' | 'free_trial' | 'paid')[]): string => {
  if (!types || types.length === 0) {
    return 'N/A';
  }

  const formattedTypes = types.map(type => {
    switch (type) {
      case 'free':
        return 'Free';
      case 'free_trial':
        return 'Free-Trial';
      case 'paid':
        return 'Paid';
      default:
        return type;
    }
  });

  return formattedTypes.join(', ');
};

const getSourceConfig = (source: string): { label: string; icon: React.ReactNode } => {
  const config: Record<string, { label: string; icon: React.ReactNode }> = {
    only_fans: { label: 'OnlyFans', icon: <OnlyFansIc size={14} fill="#A8A8B0" /> },
    reddit: { label: 'Reddit', icon: <IcReddit size={14} /> },
    instagram: { label: 'Instagram', icon: <InstagramIc size={14} fill="#A8A8B0" /> },
    tiktok: { label: 'TikTok', icon: <IcTikTok size={14} /> },
    google: { label: 'Google', icon: <GoogleIc size={14} fill="#A8A8B0" /> },
    telegram: { label: 'Telegram', icon: <IcTg size={14} /> },
    twitter: { label: 'Twitter', icon: <TwitterIc size={14} fill="#A8A8B0" /> },
    youtube: { label: 'YouTube', icon: <IcYoutube size={14} /> },
    another: { label: 'Другие', icon: <IcAnotherSoc size={14} /> },
  };
  return config[source] || { label: source, icon: <IcAnotherSoc size={14} /> };
};

interface DashboardOffersTableProps {
  offers: ApiOffer[];
  onStatusChange?: (offerId: string, status: 'active' | 'deactivated') => void;
  onApproveOffer?: (offerId: string) => void;
  onRejectOffer?: (offerId: string) => void;
}

const DashboardOffersTable: React.FC<DashboardOffersTableProps> = ({ offers, onStatusChange, onApproveOffer, onRejectOffer }) => {
  const { t } = useTranslations();

  const handleAction = (action: string, offerId: string) => {
    switch (action) {
      case 'deactivate':
        onStatusChange?.(offerId, 'deactivated');
        break;
      case 'activate':
        onStatusChange?.(offerId, 'active');
        break;
      case 'approve':
        onApproveOffer?.(offerId);
        break;
      case 'reject':
        onRejectOffer?.(offerId);
        break;
    }
  };

  const renderCell = (offer: ApiOffer, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={offer.id}>
            {offer.id.slice(-4)}
          </IdView>
        );

      case 'marketer':
        const { partner } = offer;
        const avatarUrl = partner.avatar
        const levelConfig = getMarketerLevelConfig(partner.lvl);

        return (
          <div className="flex items-center gap-[16px]">

            <Avatar
              src={avatarUrl}
              showFallback
              name={partner.name}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200 uppercase text-[14px] font-medium"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[4px]">
              <div className="flex items-center gap-[4px]">
                <Typography variant="body1" className="font-semibold text-[#11181C]">
                  {partner.name}
                </Typography>
                <Chip
                  className="h-[24px] px-[8px] text-[12px] font-normal"
                  style={{
                    backgroundColor: levelConfig.bgColor,
                    color: levelConfig.textColor,
                  }}
                >
                  {levelConfig.label}
                </Chip>
                <Tooltip
                  content={
                    <div className="px-3 py-2">
                      <div className="text-[12px] font-medium text-[#11181C] mb-2">Данные оффера</div>
                      <Typography variant="label3" className="!font-semibold !text-[#11181C]">
                        {formatCampaignTypes(offer.campaign_types)}
                      </Typography>
                      <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 2.5V6H9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 10C8.20914 10 10 8.20914 10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{formatTimeAgo(offer.created_at, t)}</span>
                      </div>
                      {offer.subs && offer.subs.length > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                          <MoneyIc/>
                          <span>${parseFloat(offer.subs[0].price).toFixed(2)} /фан</span>
                        </div>
                      )}
                      {offer.subs_min !== undefined && offer.subs_max !== undefined && (
                        <div className="flex items-center gap-1 text-[11px] text-[#71717A] mt-1">
                          <UserCheckIc/>
                          <span>{offer.subs_min}-{offer.subs_max}</span>
                        </div>
                      )}
                    </div>
                  }
                  placement="right"
                  classNames={{
                    base: "py-0 px-0",
                    content: "py-0 px-0 bg-white shadow-lg rounded-lg border border-[#E4E4E7]"
                  }}
                >
                  <div className="cursor-pointer">
                    <InfoCircle/>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        );

      case 'trafficSource':
        const sourceConfig = getSourceConfig(offer.source);
        return (
          <div className="flex items-center gap-[6px] border-[#D4D4D8] border-[1.4px] h-[24px] px-[8px] rounded-xl w-fit bg-transparent">
            <div className="w-[14px] h-[14px] flex items-center justify-center">
              {sourceConfig.icon}
            </div>
            <Typography variant="body3" className="text-[12px]">
              {sourceConfig.label}
            </Typography>
          </div>
        );

      case 'status':
        const statusConfig = getStatusConfig(offer.status);
        return (
          <Chip
            className="h-[24px] px-[4px] text-[12px] font-normal bg-zinc-100"
            style={{
              backgroundColor: '#F4F4F5',
              color: '#11181C',
            }}
          >
            <div className="flex items-center gap-[4px]">
              <div
                className="w-[7px] h-[7px] rounded-full"
                style={{ backgroundColor: statusConfig.dotColor }}
              />
              <span>{statusConfig.label}</span>
            </div>
          </Chip>
        );

      case 'actions':
        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => handleAction(String(key), offer.id)}
            >
              {offer.status === 'moderation' ? (
                <>
                  <DropdownItem key="approve" className="text-success" color="success">
                    Одобрить
                  </DropdownItem>
                  <DropdownItem key="reject" className="text-danger" color="danger">
                    Отклонить
                  </DropdownItem>
                </>
              ) : offer.status === 'active' ? (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  Деактивировать
                </DropdownItem>
              ) : (
                <DropdownItem key="activate" className="text-success" color="success">
                  Активировать
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );

      default:
        return null;
    }
  };

  return (
    <UniversalTable
      data={offers}
      columns={columns}
      renderCell={renderCell}
      emptyContent="Нет доступных офферов"
    />
  );
};

export default DashboardOffersTable;
